import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { X_API_KEY } from "../../constants";
import { API_URLS } from "../../config/apiRoutes";

export const useCameraRecorderData = () => {
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const navigate = useNavigate();
  const streamRef = useRef(null);

  const [recording, setRecording] = useState(false);
  const [progress, setProgress] = useState(0);

  // Progress bar config
  const borderRadius = 40; // px
  const strokeWidth = 6;
  const svgSize = 400;
  const rectSize = svgSize - strokeWidth;
  const rectPos = strokeWidth / 2;
  const dashArray =
    2 * (rectSize + rectSize - 2 * borderRadius) + 2 * Math.PI * borderRadius;

  // Progress animation state

  const dashOffset = useMemo(() => {
    return dashArray * (1 - progress);
  }, [progress, dashArray]);

  const saveBBPSTxnId = async () => {
    const params = new URLSearchParams(window.location.search);
    const bbpsId = params.get("bbpsid") || "";

    axios(API_URLS.ADD_PRIMARY_BBPS_ID, {
      method: "POST",
      params: { primarybbpsid: bbpsId },
      headers: {
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (res?.data?.success === 200) {
          console.log("BBPS ID added successfully");
        }
      })
      .catch((e) => {
        console.error("BBPS ID not added. Please try again");
      });
  };

  // Progress bar percentage around the video component
  useEffect(() => {
    let start = Date.now();
    let animationFrame;
    const duration = 30000; // 30 seconds
    function animate() {
      const elapsed = Date.now() - start;
      let nextProgress = Math.min(elapsed / duration, 1);
      setProgress(nextProgress);
      if (nextProgress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    }

    animate();

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, []);

  useEffect(() => {
    const startCamera = async () => {
      try {
        const isLargeScreen = window.innerWidth >= 1024;
        const facingMode = isLargeScreen ? "user" : "environment";

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: true,
        });

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }

        const mediaRecorder = new MediaRecorder(stream);
        let chunks = [];

        mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: "video/mp4" });
          navigate("/", { state: { videoBlob: blob } });
        };

        mediaRecorder.start();
        setRecording(true);
        mediaRecorderRef.current = mediaRecorder;
      } catch (err) {
        console.error("Camera error:", err);
        alert("Failed to access camera");
        navigate("/");
      }
    };

    saveBBPSTxnId();
    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [navigate]);

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    }
  };

  return {
    videoRef,
    stopRecording,
    borderRadius,
    rectPos,
    rectSize,
    strokeWidth,
    dashArray,
    dashOffset,
  };
};
